import {
  memo,
  useCallback,
  useMemo,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import styled from "styled-components";

import { TooltipIconButton, type IconButtonSize } from "../IconButton";
import { Slider } from "../Slider";
import {
  SkipToStartIcon,
  SkipToEndIcon,
  PauseIcon,
  PlayIcon,
  StepBackwardIcon,
  StepForwardIcon,
  StopIcon,
  SyncIcon,
} from "../../icons/glyphs";
import {
  resolvePlaybackCanStart,
  resolvePlaybackCapabilities,
  resolvePlaybackSeekRange,
  useVideoTimeline,
  type PlaybackTransport,
} from "./playbackTransport";
import { PlaybackRateSelect } from "./PlaybackRateSelect";
import { consumePlaybackToggleShortcut } from "./playbackShortcut";
import { isPlaybackSpaceBlocked, usePlaybackSpaceShortcut } from "./usePlaybackSpaceShortcut";
import { BUFFERING_INDICATOR_DELAY_MS, useDelayedFlag } from "./useDelayedFlag";
import { Spinner } from "../Spinner";
import { SrOnly } from "../foundations/srOnly";

const DEFAULT_STEP_SECONDS = 0.1;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/**
 * 단일 `VideoSurface`와 `SyncedVideoGrid` 양쪽을 위한 공용 재생 bar. `PlaybackTransport`에
 * 바인딩한다: 컨트롤은 명령(command, `toggle`/`seekTo…`/`reset`)을 호출하고, 시간표시·슬라이더는
 * `useVideoTimeline`로 텔레메트리(telemetry)를 구독한다. bar는 타임라인 상태를 소유하거나 에코하지
 * 않는다 — 옛 controlled `state`/`onStateChange` 왕복(round-trip)을 걷어낸 결과다
 * (RFC-video-primitives §"Playback stability contracts").
 *
 * - 멀티채널: `useSyncedVideoTimeline` api를 `transport`로 그대로 넘긴다.
 * - 단일 controlled `VideoSurface`: 그 `state`/`onStateChange`를 `useControlledTransport`로
 *   감싸 그 결과를 넘긴다.
 *
 * c6는 외부 아이콘 패키지를 번들하지 않는다. 기본 아이콘은 c6 UI glyph를 쓰며, 소비자가
 * `icons` prop으로 교체할 수 있다.
 */

export interface VideoPlaybackBarIcons {
  toStart: ReactNode;
  stepBackward: ReactNode;
  play: ReactNode;
  pause: ReactNode;
  stop: ReactNode;
  stepForward: ReactNode;
  toEnd: ReactNode;
  goLive?: ReactNode;
}

export interface VideoPlaybackBarLabels {
  toStart: string;
  stepBackward: string;
  play: string;
  pause: string;
  stop: string;
  stepForward: string;
  toEnd: string;
  seek: string;
  goLive?: string;
  live?: string;
  buffering?: string;
  waiting?: string;
  playbackRate?: string;
}

export type VideoPlaybackBarLayout = "inline" | "stacked";
export type VideoPlaybackBarTransportControls = "compact" | "full";

export interface VideoPlaybackBarProps {
  /** 타임라인: `useSyncedVideoTimeline` api 또는 `useControlledTransport` 결과. */
  transport: PlaybackTransport;
  /** 앞/뒤 이동 시 한 칸 버튼이 움직이는 초. 기본 0.1. */
  stepSeconds?: number;
  /**
   * `inline`은 한 줄 배치다. 버퍼 대기 중에는 Spinner만 보이고 상태 문구는 스크린 리더에 전달한다.
   * `stacked`는 상태 문구도 보인다. 재생 위치를 조절하는 슬라이더는 둘째 줄의 전체 폭을 쓴다.
   * 기본값은 `inline`이다.
   */
  layout?: VideoPlaybackBarLayout;
  /**
   * Transport 버튼 구성 preset. `compact`는 재생/일시정지·정지와 조건부 go-live만,
   * `full`은 step·처음/끝 이동까지 모두 보여준다. 기본 `full`.
   */
  transportControls?: VideoPlaybackBarTransportControls;
  size?: IconButtonSize;
  disabled?: boolean;
  /**
   * Opt-in Space scope: a containing player element or dialog. Native controls retain Space.
   * Null, hidden, closing, or covered scopes are inactive. Omit to keep seek-only shortcuts.
   */
  keyboardScopeRef?: RefObject<HTMLElement | null>;
  /** hover/focus 시 각 컨트롤 라벨로 툴팁을 보여줄지. 기본 true. */
  showTooltips?: boolean;
  /** 컨트롤 옆에 `현재 / 전체` 시간 표시를 보여줄지. 기본 true. */
  showTime?: boolean;
  /** transport에 바인딩된 공용 배속 selector를 보여줄지. 기본 false. */
  showPlaybackRate?: boolean;
  /** 배속 selector 옵션. 현재 transport 값은 목록에 없어도 자동으로 포함된다. */
  playbackRates?: readonly number[];
  formatTime?: (seconds: number) => string;
  icons?: Partial<VideoPlaybackBarIcons>;
  labels?: Partial<VideoPlaybackBarLabels>;
  className?: string;
  style?: CSSProperties;
}

const Root = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 0.4rem;
`;

const InlineControls = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
`;

const StackedControls = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.4rem;
`;

const StackedTopRow = styled.div`
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0.5rem;
  row-gap: 0.35rem;
`;

const Buttons = styled.div<{ $wrap: boolean }>`
  display: flex;
  min-width: 0;
  flex: 0 1 auto;
  flex-wrap: ${({ $wrap }) => ($wrap ? "wrap" : "nowrap")};
  align-items: center;
  gap: 0.25rem;
`;

const TrackArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const TrailingCluster = styled.div`
  display: flex;
  min-width: 0;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-left: auto;
`;

const TimeReadout = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 1;
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-variant-numeric: tabular-nums;
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  color: ${({ theme }) => theme.color.text.subtle};
  white-space: nowrap;
`;

const PlaybackStatus = styled.span`
  display: inline-flex;
  min-width: 1em;
  flex: 0 1 auto;
  align-items: center;
  gap: 0.35rem;
  overflow: hidden;
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  white-space: nowrap;
`;

const PlaybackStatusText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Transport controls use the shared c6 UI glyphs (Lucide-outline family).
// Consumers may override any icon via the `icons` prop.
const DEFAULT_ICONS: Required<VideoPlaybackBarIcons> = {
  toStart: <SkipToStartIcon />,
  stepBackward: <StepBackwardIcon />,
  play: <PlayIcon />,
  pause: <PauseIcon />,
  stop: <StopIcon />,
  stepForward: <StepForwardIcon />,
  toEnd: <SkipToEndIcon />,
  goLive: <SyncIcon />,
};

const DEFAULT_LABELS: Required<VideoPlaybackBarLabels> = {
  toStart: "Skip to start",
  stepBackward: "Step backward",
  play: "Play",
  pause: "Pause",
  stop: "Stop",
  stepForward: "Step forward",
  toEnd: "Skip to end",
  seek: "Seek",
  goLive: "Go live",
  live: "Live",
  buffering: "Buffering media",
  waiting: "Waiting for media",
  playbackRate: "Playback speed",
};

/** 기본 `M:SS.t` 포맷터(0.1초 단위로 움직일 때 1/10초 표시가 도움이 된다). */
export function formatPlaybackTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const totalTenths = Math.floor(seconds * 10);
  const minutes = Math.floor(totalTenths / 600);
  const secs = Math.floor(totalTenths / 10) % 60;
  const tenths = totalTenths % 10;
  return `${minutes}:${String(secs).padStart(2, "0")}.${tenths}`;
}

interface TransportButtonsProps {
  playing: boolean;
  controls: VideoPlaybackBarTransportControls;
  wrap: boolean;
  size: IconButtonSize;
  showTooltips: boolean;
  icons: VideoPlaybackBarIcons;
  labels: Required<VideoPlaybackBarLabels>;
  playDisabled: boolean;
  stopDisabled: boolean;
  backDisabled: boolean;
  forwardDisabled: boolean;
  followSupported: boolean;
  followingLive: boolean;
  followDisabled: boolean;
  onTogglePlay: () => void;
  onStop: () => void;
  onStepBackward: () => void;
  onStepForward: () => void;
  onToStart: () => void;
  onToEnd: () => void;
  onGoLive: () => void;
}

/**
 * 트랜스포트 버튼은 거친 상태(playing + 경계 disabled 플래그)에만 의존하고, 고빈도
 * currentTime엔 의존하지 않는다. memo로 감싸면 transport 버튼 + 툴팁 서브트리가 매 재생 프레임마다
 * 재조정되지 않는다 — 슬라이더와 시간 표시만 프레임 단위로 리렌더된다. 핸들러는 참조가 안정적이어야
 * 한다(`VideoPlaybackBar`의 stable handler 패턴 참고).
 */
const TransportButtons = memo(function TransportButtons({
  playing,
  controls,
  wrap,
  size,
  showTooltips,
  icons,
  labels,
  playDisabled,
  stopDisabled,
  backDisabled,
  forwardDisabled,
  followSupported,
  followingLive,
  followDisabled,
  onTogglePlay,
  onStop,
  onStepBackward,
  onStepForward,
  onToStart,
  onToEnd,
  onGoLive,
}: TransportButtonsProps) {
  const tip = (content: string) =>
    showTooltips ? ({ content, placement: "top" } as const) : undefined;

  return (
    <Buttons $wrap={wrap}>
      <TooltipIconButton
        size={size}
        variant="solid"
        aria-label={playing ? labels.pause : labels.play}
        tooltip={tip(playing ? labels.pause : labels.play)}
        disabled={playDisabled}
        onClick={onTogglePlay}
      >
        {playing ? icons.pause : icons.play}
      </TooltipIconButton>
      {/* 보조 transport는 IconButton의 암묵적 ghost 기본값에 기대지 않고, 한 그룹으로 읽히는
          bounded control을 유지하도록 surface로 고정한다. */}
      <TooltipIconButton
        size={size}
        variant="surface"
        aria-label={labels.stop}
        tooltip={tip(labels.stop)}
        disabled={stopDisabled}
        onClick={onStop}
      >
        {icons.stop}
      </TooltipIconButton>
      {controls === "full" ? (
        <>
          <TooltipIconButton
            size={size}
            variant="surface"
            aria-label={labels.stepBackward}
            tooltip={tip(labels.stepBackward)}
            disabled={backDisabled}
            onClick={onStepBackward}
          >
            {icons.stepBackward}
          </TooltipIconButton>
          <TooltipIconButton
            size={size}
            variant="surface"
            aria-label={labels.stepForward}
            tooltip={tip(labels.stepForward)}
            disabled={forwardDisabled}
            onClick={onStepForward}
          >
            {icons.stepForward}
          </TooltipIconButton>
          <TooltipIconButton
            size={size}
            variant="surface"
            aria-label={labels.toStart}
            tooltip={tip(labels.toStart)}
            disabled={backDisabled}
            onClick={onToStart}
          >
            {icons.toStart}
          </TooltipIconButton>
          <TooltipIconButton
            size={size}
            variant="surface"
            aria-label={labels.toEnd}
            tooltip={tip(labels.toEnd)}
            disabled={forwardDisabled}
            onClick={onToEnd}
          >
            {icons.toEnd}
          </TooltipIconButton>
        </>
      ) : null}
      {followSupported ? (
        <TooltipIconButton
          size={size}
          variant="surface"
          aria-label={followingLive ? labels.live : labels.goLive}
          tooltip={tip(followingLive ? labels.live : labels.goLive)}
          aria-current={followingLive ? "true" : undefined}
          disabled={followDisabled}
          onClick={onGoLive}
        >
          {icons.goLive}
        </TooltipIconButton>
      ) : null}
    </Buttons>
  );
});

/** 클릭 시점에 transport에서 읽는 현재 시각(클로저에 캡처하지 않는다). */
function readCurrent(transport: PlaybackTransport): number {
  const { currentTimeSeconds } = transport.getSnapshot();
  return Number.isFinite(currentTimeSeconds) ? Math.max(0, currentTimeSeconds) : 0;
}

export function VideoPlaybackBar({
  transport,
  stepSeconds = DEFAULT_STEP_SECONDS,
  layout = "inline",
  transportControls = "full",
  size = "md",
  disabled = false,
  keyboardScopeRef,
  showTooltips = true,
  showTime = true,
  showPlaybackRate = false,
  playbackRates,
  formatTime = formatPlaybackTime,
  icons,
  labels,
  className,
  style,
}: VideoPlaybackBarProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const mergedIcons = useMemo<Required<VideoPlaybackBarIcons>>(
    () => ({ ...DEFAULT_ICONS, ...icons }),
    [icons],
  );
  const mergedLabels = useMemo<Required<VideoPlaybackBarLabels>>(
    () => ({ ...DEFAULT_LABELS, ...labels }),
    [labels],
  );

  const stepRef = useRef(stepSeconds);
  stepRef.current = stepSeconds;

  // 텔레메트리(telemetry) 구독: 버튼 행은 거친 상태만 읽어(프레임마다 memo 유지) 재조정되지 않고,
  // 슬라이더·시간 표시만 currentTime을 읽어 프레임 단위로 재조정된다.
  const playing = useVideoTimeline(transport, (s) => s.playing);
  const durationSeconds = useVideoTimeline(transport, (s) => s.durationSeconds);
  const currentTimeSeconds = useVideoTimeline(transport, (s) => s.currentTimeSeconds);
  const seekSupported = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackCapabilities(snapshot).seek,
  );
  const playSupported = useVideoTimeline(transport, resolvePlaybackCanStart);
  const live = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackCapabilities(snapshot).live,
  );
  const followingLive = useVideoTimeline(transport, (snapshot) => snapshot.followingLive);
  const status = useVideoTimeline(transport, (snapshot) => snapshot.status);
  const seekRangeStart = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackSeekRange(snapshot).startSeconds,
  );
  const seekRangeEnd = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackSeekRange(snapshot).endSeconds,
  );

  // 명령(command)은 (안정적인) transport에만 의존하므로, currentTime이 진행했다는 이유로
  // memo된 버튼 행이 리렌더되지 않는다.
  const seekTo = useCallback((seconds: number) => transport.seekToSeconds(seconds), [transport]);
  const togglePlay = useCallback(() => transport.toggle(), [transport]);
  const stop = useCallback(() => transport.reset(), [transport]);
  const stepBackward = useCallback(
    () => transport.seekToSeconds(readCurrent(transport) - stepRef.current),
    [transport],
  );
  const stepForward = useCallback(
    () => transport.seekToSeconds(readCurrent(transport) + stepRef.current),
    [transport],
  );
  const toStart = useCallback(
    () => transport.seekToSeconds(resolvePlaybackSeekRange(transport.getSnapshot()).startSeconds),
    [transport],
  );
  const toEnd = useCallback(
    () => transport.seekToSeconds(resolvePlaybackSeekRange(transport.getSnapshot()).endSeconds),
    [transport],
  );
  const goLive = useCallback(() => transport.goLive?.(), [transport]);

  const hasDuration = Number.isFinite(durationSeconds) && durationSeconds > 0;
  const current = Number.isFinite(currentTimeSeconds) ? Math.max(0, currentTimeSeconds) : 0;
  const seekDisabled = disabled || !hasDuration || !seekSupported;
  const playbackShortcutAvailable = !seekDisabled && (playing || playSupported);
  const atStart = current <= seekRangeStart;
  const atEnd = hasDuration && current >= seekRangeEnd;
  const followSupported = live && followingLive !== undefined && transport.goLive !== undefined;
  // buffering은 재생·seek마다 생기는 짧은 멈춤에 깜빡이지 않게 늦게 보인다. producer 대기(waiting)는
  // 곧바로 보인다.
  const bufferingVisible = useDelayedFlag(
    playing && status === "buffering",
    BUFFERING_INDICATOR_DELAY_MS,
  );
  const waitingLabel =
    playing && status === "waiting"
      ? mergedLabels.waiting
      : bufferingVisible
        ? mergedLabels.buffering
        : null;
  const handleSeekKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
      if (
        keyboardScopeRef &&
        rootRef.current &&
        isPlaybackSpaceBlocked(rootRef.current, event.currentTarget)
      )
        return;
      consumePlaybackToggleShortcut(event, transport, !playbackShortcutAvailable);
    },
    [keyboardScopeRef, playbackShortcutAvailable, transport],
  );

  const handleScopeKeyDown = useCallback(
    (event: globalThis.KeyboardEvent) =>
      consumePlaybackToggleShortcut(event, transport, disabled || !hasDuration),
    [disabled, hasDuration, transport],
  );
  usePlaybackSpaceShortcut(
    rootRef,
    handleScopeKeyDown,
    !disabled && hasDuration && (playing || playSupported),
    keyboardScopeRef,
  );

  const transportButtons = (
    <TransportButtons
      playing={playing}
      controls={transportControls}
      wrap={layout === "stacked"}
      size={size}
      showTooltips={showTooltips}
      icons={mergedIcons}
      labels={mergedLabels}
      playDisabled={disabled || !hasDuration || (!playing && !playSupported)}
      stopDisabled={disabled || (atStart && !playing)}
      backDisabled={seekDisabled || atStart}
      forwardDisabled={seekDisabled || atEnd}
      followSupported={followSupported}
      followingLive={followingLive === true}
      followDisabled={disabled || !hasDuration}
      onTogglePlay={togglePlay}
      onStop={stop}
      onStepBackward={stepBackward}
      onStepForward={stepForward}
      onToStart={toStart}
      onToEnd={toEnd}
      onGoLive={goLive}
    />
  );
  const playbackStatus = waitingLabel ? (
    <PlaybackStatus role="status" aria-live="polite" data-playback-status={status}>
      <Spinner size="sm" />
      {layout === "inline" && status === "buffering" ? (
        <SrOnly>{waitingLabel}</SrOnly>
      ) : (
        <PlaybackStatusText>{waitingLabel}</PlaybackStatusText>
      )}
    </PlaybackStatus>
  ) : null;
  const track = (
    <TrackArea>
      <Slider
        aria-label={mergedLabels.seek}
        aria-keyshortcuts={playbackShortcutAvailable ? "Space" : undefined}
        fullWidth
        min={seekRangeStart}
        max={hasDuration ? Math.max(seekRangeStart, seekRangeEnd) : 1}
        step="any"
        trackSize="sm"
        value={hasDuration ? clamp(current, seekRangeStart, seekRangeEnd) : 0}
        disabled={seekDisabled}
        onKeyDown={handleSeekKeyDown}
        onValueChange={seekTo}
      />
    </TrackArea>
  );
  const trailingControls =
    showTime || showPlaybackRate ? (
      <TrailingCluster>
        {showTime ? (
          <TimeReadout>
            {formatTime(current)} / {formatTime(hasDuration ? durationSeconds : 0)}
          </TimeReadout>
        ) : null}
        {showPlaybackRate ? (
          <PlaybackRateSelect
            transport={transport}
            label={mergedLabels.playbackRate}
            rates={playbackRates}
            size={size === "sm" ? "sm" : "md"}
            disabled={disabled || !hasDuration}
          />
        ) : null}
      </TrailingCluster>
    ) : null;

  return (
    <Root ref={rootRef} className={className} style={style}>
      {layout === "stacked" ? (
        <StackedControls>
          <StackedTopRow>
            {transportButtons}
            {playbackStatus}
            {trailingControls}
          </StackedTopRow>
          {track}
        </StackedControls>
      ) : (
        <InlineControls>
          {transportButtons}
          {playbackStatus}
          {track}
          {trailingControls}
        </InlineControls>
      )}
    </Root>
  );
}

VideoPlaybackBar.displayName = "VideoPlaybackBar";
